import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { enablePush, isIos, pushSupported, syncPush } from "@/lib/push";

// 🔔 A small card that asks for phone alerts, only on pages where the person is already doing something good.
const SHOW_ON = ["/payment-success", "/dashboard", "/track-order"];
const DISMISS_KEY = "mad_push_dismissed_at";
const DISMISS_DAYS = 7;

function dismissedRecently(): boolean {
  try {
    const t = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return t > 0 && Date.now() - t < DISMISS_DAYS * 86400000;
  } catch {
    return false;
  }
}

export default function NotifyPrompt() {
  const { pathname, search } = useLocation();
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [done, setDone] = useState(false);

  const onRoute = SHOW_ON.some((p) => pathname === p || pathname.startsWith(p + "/"));
  const reference = new URLSearchParams(search).get("reference") || "";

  // Show the card after a short wait, if it makes sense
  useEffect(() => {
    setVisible(false);
    if (!onRoute || dismissedRecently()) return;
    if (!pushSupported() && !isIos()) return;
    if (pushSupported() && Notification.permission !== "default") return;
    const t = setTimeout(() => setVisible(true), 4000);
    return () => clearTimeout(t);
  }, [onRoute, pathname]);

  // Quietly refresh tags for people who already allowed alerts
  useEffect(() => {
    if (!onRoute || !pushSupported() || Notification.permission !== "granted") return;
    const key = "mad_push_synced_" + pathname;
    try {
      if (sessionStorage.getItem(key)) return;
    } catch {
      /* ignore */
    }
    const t = setTimeout(() => {
      syncPush(reference || undefined).finally(() => {
        try {
          sessionStorage.setItem(key, "1");
        } catch {
          /* ignore */
        }
      });
    }, 3000);
    return () => clearTimeout(t);
  }, [onRoute, pathname, reference]);

  if (!visible) return null;

  const turnOn = async () => {
    setBusy(true);
    setMessage("");
    try {
      const r = await enablePush(reference || undefined);
      if (r.ok) {
        setDone(true);
        setMessage("✅ Alerts are on. Thank you!");
        setTimeout(() => setVisible(false), 3500);
      } else if (r.reason === "ios-home-screen") {
        setMessage("On iPhone: tap the Share button, choose Add to Home Screen, then open the site from your home screen and tap Turn on again.");
      } else if (r.reason === "denied") {
        setMessage("Alerts are blocked. Open your browser settings for this site and choose Allow.");
      } else if (r.reason === "unsupported") {
        setMessage("This browser can't show alerts. Try Chrome on Android.");
      } else {
        setMessage("Sorry, something went wrong. Please try again.");
      }
    } catch {
      setMessage("Sorry, something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const notNow = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
    setVisible(false);
  };

  return (
    <div className="fixed z-40 left-4 right-4 sm:left-auto sm:right-4 sm:w-80 bg-white rounded-2xl shadow-xl border border-foreground-100 p-4" style={{ bottom: "calc(6rem + var(--bar-h, 0px))" }}>
      <p className="text-sm font-bold text-foreground-900">🔔 Get alerts on your phone</p>
      <p className="text-xs text-foreground-600 mt-1">Know when your data is delivered and hear about offers first. You can turn it off any time.</p>
      {message && <p className="text-xs text-primary-700 mt-2">{message}</p>}
      {!done && (
        <div className="flex gap-2 mt-3">
          <button disabled={busy} onClick={turnOn} className="px-4 py-2 rounded-full text-xs font-semibold bg-primary-600 text-white cursor-pointer disabled:opacity-50">
            {busy ? "Please wait..." : "Turn on"}
          </button>
          <button onClick={notNow} className="px-4 py-2 rounded-full text-xs font-semibold bg-background-100 text-foreground-700 cursor-pointer">Not now</button>
        </div>
      )}
    </div>
  );
}
